import {
  MINICHEF_CONTRACT_ADDRESSES,
  FERROFARMV2_CONTRACT_ADDRESSES,
  POOLS_MAP,
} from "../constants"
import React, { ReactElement, useCallback, useState } from "react"

import { BigNumber } from "@ethersproject/bignumber"
import { Contract } from "ethcall"
import MINICHEF_CONTRACT_ABI from "../constants/abis/miniChef.json"
import FERROFARMV2_CONTRACT_ABI from "../constants/abis/ferroFarmV2.json"
import { MiniChef } from "../../types/ethers-contracts/MiniChef"
import { FerroFarmV2 } from "../../types/ethers-contracts/FerroFarmV2"
import { MulticallContract } from "../types/ethcall"
import { Zero } from "@ethersproject/constants"
import { getMulticallProvider } from "../utils"
import { useActiveWeb3React } from "../hooks"
import usePoller from "../hooks/usePoller"

export type PoolsRewards = { [poolName: string]: BigNumber }
export type PoolsRewardsV2 = {
  [poolName: string]: {
    tokens: string[]
    earnings: BigNumber[]
  }
}

export const RewardsBalancesContext = React.createContext<
  [PoolsRewards, PoolsRewardsV2]
>([{}, {}])

export default function RewardsBalancesProvider({
  children,
}: React.PropsWithChildren<unknown>): ReactElement {
  const poolsRewardsBalances = usePoolsRewardBalances()
  const poolsRewardTokens = usePoolsRewardTokens()

  return (
    <RewardsBalancesContext.Provider
      value={[poolsRewardsBalances, poolsRewardTokens]}
    >
      {children}
    </RewardsBalancesContext.Provider>
  )
}

function usePoolsRewardBalances() {
  const { chainId, account, library } = useActiveWeb3React()
  const [balances, setBalances] = useState<PoolsRewards>({})

  const fetchBalances = useCallback(async () => {
    if (!library || !chainId || !account) {
      setBalances({})
      return
    }
    const ethcallProvider = await getMulticallProvider(library, chainId)
    const pools = Object.values(POOLS_MAP).filter(
      ({ addresses, rewardPids }) =>
        chainId && rewardPids[chainId] !== null && addresses[chainId],
    )
    if (pools.length === 0) return

    const farmMulticallContract = new Contract(
      MINICHEF_CONTRACT_ADDRESSES[chainId],
      MINICHEF_CONTRACT_ABI,
    ) as MulticallContract<MiniChef>
    const pendingFERCalls = pools.map(({ rewardPids }) => {
      return farmMulticallContract.pendingFer(
        rewardPids[chainId] as number,
        account,
      )
    })

    try {
      const fetchedBalances = await ethcallProvider.all(pendingFERCalls, {})
      setBalances(
        fetchedBalances.reduce((acc, balance, i) => {
          const { name } = pools[i]
          return balance != null && balance.gt(Zero)
            ? { ...acc, [name]: balance }
            : acc
        }, {} as PoolsRewards),
      )
    } catch (e) {
      console.error(e)
    }
  }, [library, chainId, account])

  usePoller(() => void fetchBalances(), 5000)

  return balances
}

function usePoolsRewardTokens() {
  const { chainId, account, library } = useActiveWeb3React()
  const [tokenRewards, setTokenRewards] = useState<PoolsRewardsV2>({})

  const fetchTokenRewards = useCallback(async () => {
    if (!library || !chainId || !account) {
      setTokenRewards({})
      return
    }
    const ethcallProvider = await getMulticallProvider(library, chainId)
    const poolsV2 = Object.values(POOLS_MAP).filter(
      ({ addresses, rewardPids, version }) =>
        chainId &&
        rewardPids[chainId] !== null &&
        addresses[chainId] &&
        version === 2,
    )
    if (poolsV2.length === 0) return

    const farmV2MulticallContract = new Contract(
      FERROFARMV2_CONTRACT_ADDRESSES[chainId],
      FERROFARMV2_CONTRACT_ABI,
    ) as MulticallContract<FerroFarmV2>
    const pendingTokensCalls = poolsV2.map(({ rewardPids }) =>
      farmV2MulticallContract.pendingTokens(
        rewardPids[chainId] as number,
        account,
      ),
    )
    try {
      const fetchedRewards = await ethcallProvider.all(pendingTokensCalls, {})
      const formatRewards = fetchedRewards
        .map(([tokenAddrs, earnings]) => {
          return {
            tokens: tokenAddrs,
            earnings,
          }
        })
        .reduce((acc, rewards, i) => {
          const { name } = poolsV2[i]
          return { ...acc, [name]: rewards }
        }, {} as PoolsRewardsV2)

      setTokenRewards(formatRewards)
    } catch (e) {
      console.error(e)
    }
  }, [library, chainId, account])

  usePoller(() => void fetchTokenRewards(), 5000)

  return tokenRewards
}
